// Airport metadata.
//
// At runtime the portal pulls names from two sources:
//
//   1. The flight search response itself — `segment.origin.airport_name`,
//      `city_name`, `country_name`, `terminal` — all come from TravClan via
//      providers/travclan/format.py. So anywhere we render a real flight
//      segment, the name is provider-supplied (not hardcoded).
//
//   2. The /v1/airports/autocomplete/?q= endpoint — backed by mercury's curated
//      Hub table. Used by the airport-picker on the Search form, and by the
//      tiny `cacheAirport()` helper below to remember names we've seen so the
//      results topbar can render "Delhi" instead of just "DEL" after the user
//      sails through the search.
//
// We keep a tiny in-memory cache of {code → city/name} so the topbar and
// confirmation-page hero can show city names without re-hitting the API.

export interface AirportInfo {
  code: string;
  city: string;
  name: string;
}

const _CACHE = new Map<string, AirportInfo>();

export function cacheAirport(info: AirportInfo) {
  if (info.code) _CACHE.set(info.code.toUpperCase(), info);
}

export function lookupAirport(code: string): AirportInfo {
  const c = (code || "").trim().toUpperCase();
  const hit = _CACHE.get(c);
  if (hit) return hit;
  return { code: c, city: c, name: "Airport" };
}

export interface AirportSearchResult {
  code: string;
  city: string;
  name: string;
}

// Fire-and-forget warming of the cache from a list of results — when a search
// returns, every visible flight has airport_code + city_name + airport_name,
// so we seed cache from those.
interface AirportEndpoint {
  airport_code?: string;
  airport_name?: string;
  city_name?: string;
}
interface CacheSeedSegment { origin: AirportEndpoint; destination: AirportEndpoint }
interface CacheSeedOption { segments: CacheSeedSegment[] }

export function warmCacheFromSearch(resp: {
  outbound?: CacheSeedOption[];
  inbound?: CacheSeedOption[];
}): void {
  const seed = (s: AirportEndpoint) => {
    if (s.airport_code) {
      cacheAirport({ code: s.airport_code, city: s.city_name || "", name: s.airport_name || "" });
    }
  };
  for (const o of resp.outbound || []) {
    for (const s of o.segments) { seed(s.origin); seed(s.destination); }
  }
  for (const o of resp.inbound || []) {
    for (const s of o.segments) { seed(s.origin); seed(s.destination); }
  }
}
